import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Gauge, ChevronUp, ChevronDown } from "lucide-react";
import { Text, Button } from "@core/index";

interface LevelSelectorProps {
  value: number;
  onChange: (value: number) => void;
}

const LevelSelector = ({ value, onChange }: LevelSelectorProps) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(value);

  const levels = [
    { value: 1, label: t("textLeveler.levels.elementary") },
    { value: 3, label: t("textLeveler.levels.intermediate") },
    { value: 5, label: t("textLeveler.levels.upperIntermediate") },
    { value: 7, label: t("textLeveler.levels.advanced") },
    { value: 9, label: t("textLeveler.levels.proficient") },
  ];

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleLevelClick = (level: number) => {
    setLocalValue(level);
    onChange(level);
  };

  const getGradientColor = (level: number) => {
    const colors = {
      1: "from-green-400 to-green-500",
      3: "from-cyan-400 to-cyan-500",
      5: "from-blue-400 to-blue-500",
      7: "from-purple-400 to-purple-500",
      9: "from-rose-400 to-rose-500",
    };
    return colors[level as keyof typeof colors] || colors[1];
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
          <Gauge className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <Text variant={"h3"}>textLeveler.selectLevel</Text>
      </div>

      <div className="relative mb-8">
        <input
          type="range"
          min="1"
          max="9"
          step={2}
          value={localValue}
          onChange={handleSliderChange}
          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
        />
        <div className="absolute -top-2 left-0.5 right-0 flex justify-between px-1">
          {[1, 3, 5, 7, 9].map((mark) => (
            <div
              onClick={() => handleLevelClick(mark)}
              key={mark}
              className={`w-1 h-4 rounded-full transition-colors cursor-pointer ${mark <= localValue ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"}`}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2">
        {levels.map((level) => (
          <Button
            type="button"
            key={level.value}
            onClick={(e) => {
              e.preventDefault();
              handleLevelClick(level.value);
            }}
            className={`relative group flex flex-col items-center ${
              level.value === localValue ? "scale-105" : "hover:scale-105"
            }`}
          >
            <div
              className={`w-full aspect-square rounded-2xl flex items-center justify-center mb-2 bg-gradient-to-br ${getGradientColor(
                level.value,
              )} ${
                level.value === localValue
                  ? "ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-800"
                  : "opacity-60 group-hover:opacity-100"
              } transition-all duration-200`}
            >
              {level.value === localValue ? (
                <ChevronUp className="w-6 h-6 text-white " />
              ) : (
                <ChevronDown className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              )}
            </div>
            {level.value === localValue && (
              <span
                className={`text-xs text-center font-medium ${
                  level.value === localValue
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {level.label}
              </span>
            )}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default LevelSelector;
